import React from 'react';
import _get from 'lodash/get';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  FormControlLabel,
  FormGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { SeriesCheckbox } from 'containers/Series/components/SeriesCheckbox';
import { CourseWorksheet } from './CourseWorksheet';
import { CourseCommentInput } from '../course-comment-input/CourseCommentInput';

const useStyles = makeStyles(theme => ({
  formGroup: {
    flexDirection: 'row',
    gap: theme.spacing(2),
    paddingRight: theme.spacing(1),
    overflowY: 'auto',
    maxHeight: 500,
  },
  formLabel: {
    margin: 0,
    padding: '10px 8px',
    width: props => (props.small ? 'calc(50% - 8px)' : 'calc(25% - 15px)'),
    borderRadius: 20,
    backgroundColor: '#EEF9FF',
    '& .MuiFormControlLabel-label': {
      ...theme.typography.pRegular,
      fontSize: props => props.small && '0.75rem',
    },

    '& .MuiCheckbox-root': {
      padding: theme.spacing(0.5),
      marginRight: theme.spacing(1),
    },
  },
  accordion: {
    border: '1px solid #E0E0E0',
    borderRadius: '8px !important',
    '&:before': {
      display: 'none',
    },
    '&:hover': {
      border: `1px solid ${theme.palette.primary.main}`,
    },

    '& + &': {
      marginTop: theme.spacing(2),
    },

    '& .MuiAccordionSummary-root': {
      padding: theme.spacing(2),
    },
  },
  accordionDetails: {
    flexDirection: 'column',
  },
  chip: {
    height: 'auto',
    backgroundColor: '#EEF9FF',
    marginLeft: 8,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,

    '& .MuiChip-label': {
      ...theme.typography.pRegular,
    },
  },
}));

export const CoreValuesWorksheet = props => {
  const classes = useStyles({ small: props.small });

  const questionRenderers = {
    checkbox: ({ question, answers, onAnswerChange }) => {
      const { name } = question;

      const onCheckBoxChange = e => {
        const current = answers[name] || [];
        const { value, checked } = e.target;
        const selected = checked
          ? [...current, value]
          : current.filter(v => v !== value);
        if (selected.length <= _get(question, 'maxCount', 5)) {
          onAnswerChange(name, selected);
        }
      };

      return (
        <FormGroup className={classes.formGroup}>
          {question.choices.map(label => {
            const checked =
              !!answers[name] &&
              Array.isArray(answers[name]) &&
              answers[name].includes(label);

            return (
              <FormControlLabel
                key={label}
                className={classes.formLabel}
                control={
                  <SeriesCheckbox
                    checked={checked}
                    onChange={onCheckBoxChange}
                    value={label}
                    color="primary"
                    inputProps={{ 'aria-label': `Checkbox ${label}` }}
                    disableRipple
                  />
                }
                label={label}
              />
            );
          })}
        </FormGroup>
      );
    },
    comment: ({ question, answers, onAnswerChange }) => {
      const { name } = question;

      const onChange = e => {
        onAnswerChange(e.target.name, e.target.value);
      };

      return (
        <CourseCommentInput
          {...question}
          value={answers[name]}
          onChange={onChange}
        />
      );
    },
    accordion: ({ question, answers, onAnswerChange }) => {
      const { name } = question;
      const onChange = (itemName, label) => e => {
        onAnswerChange(name, {
          ...answers[name],
          [itemName]: {
            ..._get(answers[name], itemName),
            [label]: e.target.value,
          },
        });
      };

      return answers.coreValues.map(label => (
        <Accordion
          key={label}
          square
          elevation={0}
          className={classes.accordion}
        >
          <AccordionSummary
            aria-controls={label}
            expandIcon={<ExpandMoreIcon />}
          >
            <Typography variant="h6" color="textPrimary">
              {label}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails} id={label}>
            <Divider />
            {question.items.map(item => (
              <CourseCommentInput
                key={item.name}
                {...item}
                value={_get(answers[name], [item.name, label])}
                onChange={onChange(item.name, label)}
              />
            ))}
          </AccordionDetails>
        </Accordion>
      ));
    },
  };

  const renderAnswer = answerValue =>
    answerValue.map(label => (
      <Chip className={classes.chip} label={label} aria-label={label} />
    ));

  return (
    <CourseWorksheet
      {...props}
      questionRenderers={questionRenderers}
      renderAnswer={renderAnswer}
    />
  );
};
